// Developer: Ankit Gupta
/**
 * Unified Active Scope Hook.
 *
 * Provides a single source of truth for the currently selected project across
 * DataPilot Migration Studio, Insight Decision Intelligence, and the Dashboard.
 *
 * Settle order:
 * 1. Global app store activeProjectId (persisted across sessions)
 * 2. Local storage keys ('dp.dashboard.project' / 'insight.scope')
 * 3. First available project returned by the API
 */
import { useEffect, useMemo } from 'react';
import { useProjects } from './useProject';
import { useAppStore } from '@/store';
import { readRememberedScope, rememberScope } from '@/pages/Insight/scopeChoice';

const DASHBOARD_KEY = 'dp.dashboard.project';

export function useActiveScope() {
  const { data: projects = [], isLoading } = useProjects();
  const activeProjectId = useAppStore((s) => s.activeProjectId);
  const setActiveProjectId = useAppStore((s) => s.setActiveProjectId);
  const currentProject = useAppStore((s) => s.currentProject);
  const setCurrentProject = useAppStore((s) => s.setCurrentProject);

  // Initialize or fallback project selection once projects load
  useEffect(() => {
    if (isLoading || projects.length === 0) return;

    let targetId: number | null = activeProjectId;

    // Check if current activeProjectId is valid. A project that is absent from
    // the list but already loaded as currentProject (ProjectLayout / useProject
    // fetched it by URL) is still a valid selection: the route is the authority
    // there, and overriding it would ping-pong with ProjectLayout's own sync
    // effect until React bails with error #185.
    if (
      targetId != null &&
      !projects.some((p) => p.id === targetId) &&
      currentProject?.id !== targetId
    ) {
      targetId = null;
    }

    // Try reading from stored keys if not yet set
    if (targetId == null) {
      try {
        const savedDash = localStorage.getItem(DASHBOARD_KEY);
        if (savedDash && projects.some((p) => p.id === Number(savedDash))) {
          targetId = Number(savedDash);
        }
      } catch {
        // ignore
      }
    }

    if (targetId == null) {
      const insightScope = readRememberedScope();
      if (insightScope?.project && projects.some((p) => p.id === insightScope.project)) {
        targetId = insightScope.project;
      }
    }

    // Default to first project
    if (targetId == null && projects.length > 0) {
      targetId = projects[0].id;
    }

    if (targetId != null && targetId !== activeProjectId) {
      setActiveProjectId(targetId);
    }
  }, [projects, isLoading, activeProjectId, currentProject, setActiveProjectId]);

  // Synchronize currentProject object when activeProjectId or projects change
  useEffect(() => {
    if (!activeProjectId || projects.length === 0) return;
    const match = projects.find((p) => p.id === activeProjectId);
    if (match && (!currentProject || currentProject.id !== match.id)) {
      setCurrentProject(match);
      try {
        localStorage.setItem(DASHBOARD_KEY, String(match.id));
        rememberScope(match.id, null);
      } catch {
        // ignore
      }
    }
  }, [activeProjectId, projects, currentProject, setCurrentProject]);

  const activeProject = useMemo(() => {
    return projects.find((p) => p.id === activeProjectId) ?? currentProject ?? null;
  }, [projects, activeProjectId, currentProject]);

  const selectProject = (id: number) => {
    setActiveProjectId(id);
    const found = projects.find((p) => p.id === id);
    if (found) {
      setCurrentProject(found);
      try {
        localStorage.setItem(DASHBOARD_KEY, String(id));
        rememberScope(id, null);
      } catch {
        // ignore
      }
    }
  };

  return {
    projects,
    activeProject,
    activeProjectId: activeProject?.id ?? activeProjectId,
    selectProject,
    isLoading,
  };
}

export default useActiveScope;
